import {
  Component,
  computed,
  DestroyRef,
  inject,
  OnInit,
  signal,
  TemplateRef,
  viewChild,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Observable } from 'rxjs';
import { NgClass } from '@angular/common';
import { ColumnDef, DataTableComponent } from '../../../../shared/components/data-table/data-table';
import { SituationTaxonomy, ResponseTaxonomy } from '../../../../shared/models/taxonomy.models';
import {
  SituationTaxonomyMember,
  ResponseTaxonomyMember,
} from '../../../../shared/models/taxonomy-member.models';
import { TaxonomyService } from '../../../../core/services/taxonomy.service';
import { TaxonomyMemberService } from '../../../../core/services/taxonomy-member.service';
import { AddTaxonomyCategoryModalComponent } from './add-taxonomy-category-modal';
import { EditTaxonomyModalComponent } from './edit-taxonomy-modal';
import { EditTaxonomyMemberModalComponent } from './edit-taxonomy-member-modal';

type TaxonomyDetail = SituationTaxonomy | ResponseTaxonomy;
type TaxonomyMember = SituationTaxonomyMember | ResponseTaxonomyMember;

@Component({
  selector: 'app-taxonomy-detail',
  imports: [
    NgClass,
    DataTableComponent,
    AddTaxonomyCategoryModalComponent,
    EditTaxonomyModalComponent,
    EditTaxonomyMemberModalComponent,
  ],
  templateUrl: './taxonomy-detail.html',
})
export class TaxonomyDetailComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private taxonomyService = inject(TaxonomyService);
  private taxonomyMemberService = inject(TaxonomyMemberService);
  private destroyRef = inject(DestroyRef);

  protected type = signal<'situation' | 'response'>('situation');
  protected taxonomyId = signal('');
  protected loading = signal(true);
  protected taxonomy = signal<TaxonomyDetail | null>(null);
  protected members = signal<TaxonomyMember[]>([]);
  protected totalMembers = signal(0);
  protected pageIndex = signal(0);
  protected readonly pageSize = 10;
  protected showAddModal = signal(false);

  protected showEditModal = signal(false);
  protected editingMember = signal<TaxonomyMember | null>(null);
  protected searchQuery = signal('');

  protected taxonomyName = computed(() => {
    const t = this.taxonomy();
    if (!t) return '';
    return 'name' in t ? (t as SituationTaxonomy).name : (t as ResponseTaxonomy).taxonomyName;
  });

  private categoryNameTpl =
    viewChild.required<TemplateRef<{ $implicit: TaxonomyMember }>>('categoryNameTpl');
  private statusTpl = viewChild.required<TemplateRef<{ $implicit: TaxonomyMember }>>('statusTpl');

  protected columns = computed<ColumnDef<TaxonomyMember>[]>(() => [
    {
      key: 'order',
      header: 'Order',
      cell: (m) => m.displayOrder ?? '—',
      headerClass: 'w-20',
      cellClass: 'text-slate-500 tabular-nums',
    },
    { key: 'name', header: 'Category Name', cellTemplate: this.categoryNameTpl() },
    { key: 'active', header: 'Status', cellTemplate: this.statusTpl() },
  ]);

  ngOnInit(): void {
    const type = this.route.snapshot.paramMap.get('type') as 'situation' | 'response';
    const id = this.route.snapshot.paramMap.get('id') ?? '';
    this.type.set(type);
    this.taxonomyId.set(id);

    const request$: Observable<TaxonomyDetail> =
      type === 'situation'
        ? this.taxonomyService.getSituationTaxonomyById(id)
        : this.taxonomyService.getResponseTaxonomyById(id);

    request$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (res) => {
        this.taxonomy.set(res);
        this.loading.set(false);
        this.loadMembers();
      },
      error: () => this.loading.set(false),
    });
  }

  protected loadTaxonomy(): void {
    const id = this.taxonomyId();
    const request$: Observable<TaxonomyDetail> =
      this.type() === 'situation'
        ? this.taxonomyService.getSituationTaxonomyById(id)
        : this.taxonomyService.getResponseTaxonomyById(id);

    request$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (res) => this.taxonomy.set(res),
    });
  }

  protected loadMembers(): void {
    const id = this.taxonomyId();
    const params = {
      pageSize: this.pageSize,
      pageIndex: this.pageIndex(),
      filter: this.searchQuery(),
    };
    const request$: Observable<{ total: number }> =
      this.type() === 'situation'
        ? this.taxonomyMemberService.getSituationTaxonomyMembers(id, params)
        : this.taxonomyMemberService.getResponseTaxonomyMembers(id, params);

    request$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (res: any) => {
        const list = res.situationTaxonomiesMembers ?? res.responseTaxonomiesMembers ?? [];
        this.members.set(list);
        this.totalMembers.set(res.total);
      },
    });
  }

  protected onPageChange(page: number): void {
    this.pageIndex.set(page - 1);
    this.loadMembers();
  }

  protected onSearchInput(event: Event): void {
    this.searchQuery.set((event.target as HTMLInputElement).value);
    this.pageIndex.set(0);
    this.loadMembers();
  }

  protected memberName(member: TaxonomyMember): string {
    if ('situationCategoryName' in member) return member.situationCategoryName;
    return member.responseCategory.name;
  }

  protected onMemberAction(member: TaxonomyMember): void {
    this.editingMember.set(member);
  }

  protected onTaxonomyRemoved(): void {
    this.router.navigate(['/configuration/categories'], { queryParams: { tab: 'taxonomies' } });
  }

  protected goBack(): void {
    this.router.navigate(['/configuration/categories'], { queryParams: { tab: 'taxonomies' } });
  }
}
